import { ChevronDown, ChevronRight, Edit2, GripVertical, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useRealEstateSystem } from '../../hooks/useRealEstateSystem';

export function RealEstateCategories() {
  const { chatbotPublicId } = useChatbot();
  const { categories, addCategory, updateCategory, deleteCategory, reorderCategories, loading } = useRealEstateSystem();
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    parent_id: null as string | null
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [addingSubcategoryFor, setAddingSubcategoryFor] = useState<string | null>(null);
  const [draggedCategory, setDraggedCategory] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [draggedCategoryIndex, setDraggedCategoryIndex] = useState<number | null>(null);

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatbotPublicId || !newCategory.name.trim()) return;

    try {
      await addCategory(chatbotPublicId, {
        name: newCategory.name,
        parent_id: newCategory.parent_id,
        description: newCategory.description.trim() || undefined
      });
      setNewCategory({ name: '', description: '', parent_id: null });
      setShowAddForm(false);
      setAddingSubcategoryFor(null);
    } catch (err) {
      console.error('Error adding category:', err);
    }
  };

  const handleUpdateCategory = async (categoryId: string, updates: { name: string; description?: string }) => {
    if (!chatbotPublicId) return;
    try {
      await updateCategory(chatbotPublicId, categoryId, updates);
      setEditingCategory(null);
    } catch (err) {
      console.error('Error updating category:', err);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!chatbotPublicId) return;
    if (window.confirm('Are you sure you want to delete this category?')) {
      await deleteCategory(chatbotPublicId, categoryId);
    }
  };

  const toggleExpand = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const handleDragStart = (index: number) => {
    setDraggedCategoryIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, index: number) => {
    e.preventDefault();
    
    if (draggedCategoryIndex === null || draggedCategoryIndex === index || !chatbotPublicId) return;
    
    const newCategories = [...categories];
    const draggedCategory = newCategories[draggedCategoryIndex];
    newCategories.splice(draggedCategoryIndex, 1);
    newCategories.splice(index, 0, draggedCategory);
    
    setDraggedCategoryIndex(null);
    await reorderCategories(chatbotPublicId, newCategories.map(c => c.id));
  };

  const handleStartEdit = (category: any) => {
    setEditingCategory(category.id);
    setEditName(category.name);
    setEditDescription(category.description || '');
  };

  const handleCancelEdit = () => {
    setEditingCategory(null);
  };

  const renderCategory = (category: any, level = 0) => {
    const hasSubcategories = categories.some(c => c.parent_id === category.id);
    const isExpanded = expandedCategories.has(category.id);

    return (
      <div key={category.id} className="space-y-2">
        <div
          className="bg-white/5 rounded-lg p-4 border border-white/10"
          style={{ marginLeft: `${level * 1.5}rem` }}
          draggable
          onDragStart={(e) => handleDragStart(categories.indexOf(category))}
          onDragOver={(e) => handleDragOver(e, categories.indexOf(category))}
          onDrop={(e) => handleDrop(e, categories.indexOf(category))}
        >
          {editingCategory === category.id ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleUpdateCategory(category.id, {
                  name: editName,
                  description: editDescription
                });
              }}
              className="space-y-3"
            >
              <input
                type="text"
                name="name"
                defaultValue={category.name}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <textarea
                name="description"
                defaultValue={category.description}
                onChange={(e) => setEditDescription(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={2}
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-3 py-1.5 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-1.5 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors"
                >
                  Save
                </button>
              </div>
            </form>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <GripVertical className="w-5 h-5 text-gray-500 cursor-move" />
                {hasSubcategories && (
                  <button
                    onClick={() => toggleExpand(category.id)}
                    className="p-1 text-gray-400 hover:text-white transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                )}
                <div>
                  <h4 className="text-white font-medium">{category.name}</h4>
                  {category.description && (
                    <p className="text-sm text-gray-400 mt-1">{category.description}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setNewCategory({ name: '', description: '', parent_id: category.id });
                    setAddingSubcategoryFor(category.id);
                    setShowAddForm(true);
                  }}
                  className="p-1 text-gray-400 hover:text-indigo-400 transition-colors"
                  title="Ajouter une sous-catégorie"
                >
                  <Plus className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleStartEdit(category)}
                  className="p-1 text-gray-400 hover:text-white transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDeleteCategory(category.id)}
                  className="p-1 text-red-400 hover:text-red-300 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Sous-catégories */}
        {hasSubcategories && isExpanded && (
          <div className="space-y-2">
            {categories
              .filter(c => c.parent_id === category.id)
              .map(subcategory => renderCategory(subcategory, level + 1))
            }
          </div>
        )}

        {/* Formulaire d'ajout de sous-catégorie */}
        {showAddForm && addingSubcategoryFor === category.id && (
          <div 
            className="bg-white/5 rounded-lg p-4 space-y-4"
            style={{ marginLeft: `${(level + 1) * 1.5}rem` }}
          >
            <form onSubmit={handleAddCategory} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Name of the subcategory
                </label>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Nom de la sous-catégorie"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description (optional)
                </label>
                <textarea
                  value={newCategory.description}
                  onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Description de la sous-catégorie"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setAddingSubcategoryFor(null);
                    setNewCategory({ name: '', description: '', parent_id: null });
                  }}
                  className="px-4 py-2 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading.categories || !newCategory.name.trim()}
                  className="px-4 py-2 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors disabled:opacity-50"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <button
        onClick={() => {
          setShowAddForm(true);
          setAddingSubcategoryFor(null);
          setNewCategory({ name: '', description: '', parent_id: null });
        }}
        className="flex items-center gap-2 px-4 py-2 bg-indigo-500/20 text-indigo-400 rounded-lg hover:bg-indigo-500/30 transition-colors w-full justify-center"
      >
        <Plus className="w-4 h-4" />
        Add a category
      </button>

      {showAddForm && !addingSubcategoryFor && (
        <form onSubmit={handleAddCategory} className="bg-white/5 rounded-lg p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Category Name
            </label>
            <input
              type="text"
              value={newCategory.name}
              onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Nom de la catégorie"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Description (optional
            </label>
            <textarea
              value={newCategory.description}
              onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Description de la catégorie"
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setNewCategory({ name: '', description: '', parent_id: null });
              }}
              className="px-4 py-2 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading.categories || !newCategory.name.trim()}
              className="px-4 py-2 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {categories
          .filter(category => !category.parent_id)
          .map(category => renderCategory(category))}
      </div>
    </div>
  );
}